import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, CardContent, Stack, InputAdornment, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon, FileDownload as FileDownloadIcon, FileUpload as FileUploadIcon, PictureAsPdf as PictureAsPdfIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import noImputableService, { type NoImputable, formatNoImputableLabel } from '../../services/noImputableService';
import imputableService from '../../services/imputableService';
import companiaService from '../../services/companiaService';
import PDFViewer from '../../components/shared/PDFViewer';
import * as XLSX from 'xlsx';

const SESSION_KEY = 'noImputables_listState';

interface ListState { page: number; rowsPerPage: number; searchTerm: string; }

/** Etiqueta de columna "Visible en", alineado al informe legacy (Ambos / Ninguno / IVarios / EVarios). */
function visibleEnReporte(v: string | null | undefined): string {
  const s = v != null ? String(v).trim() : '';
  if (!s) return 'Ninguno';
  if (/^ambos$/i.test(s)) return 'Ambos';
  if (/^ninguno$/i.test(s)) return 'Ninguno';
  if (s === 'IVarios' || /^ivarios$/i.test(s)) return 'IVarios';
  if (s === 'EVarios' || /^evarios$/i.test(s)) return 'EVarios';
  return s;
}

function formatoNombreRubroReporte(ni: NoImputable | undefined, nroRubro: number): string {
  const base = (ni?.nombreCuenta || ni?.descripcioncta || `Rubro ${nroRubro}`).trim();
  return base.toUpperCase();
}

function readSessionState(): ListState {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) return JSON.parse(raw) as ListState;
  } catch {}
  return { page: 0, rowsPerPage: 10, searchTerm: '' };
}

const NoImputables: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [noImputables, setNoImputables] = useState<NoImputable[]>([]);
  const [rubroLookup, setRubroLookup] = useState<NoImputable[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const saved = readSessionState();
  const [page, setPage] = useState(saved.page);
  const [rowsPerPage, setRowsPerPage] = useState(saved.rowsPerPage);
  const [searchTerm, setSearchTerm] = useState(saved.searchTerm);
  const [_loading, setLoading] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const previewPdfUrlRef = useRef<string | null>(null);
  previewPdfUrlRef.current = previewPdfUrl;
  const isMounted = useRef(false);

  const saveState = (p: number, rpp: number, s: string) => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ page: p, rowsPerPage: rpp, searchTerm: s }));
  };

  const fetchNoImputables = async (pageNum: number = page, pageSize: number = rowsPerPage, search: string = searchTerm) => {
    try {
      setLoading(true);
      const result = await noImputableService.getNoImputables({
        pageNumber: pageNum + 1,
        pageSize: pageSize,
        searchTerm: search
      });
      setNoImputables(result.items || []);
      setTotalCount(result.totalCount || 0);
    } catch (error: any) {
      console.error('Error al cargar no imputables:', error);
      enqueueSnackbar('Error al cargar no imputables', { variant: 'error' });
      setNoImputables([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    isMounted.current = true;
    fetchNoImputables(page, rowsPerPage, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshRubroLookup = () => {
    noImputableService.getAllNoImputablesPlanCuentas()
      .then(setRubroLookup)
      .catch(() => {});
  };

  useEffect(() => {
    refreshRubroLookup();
  }, []);

  useEffect(() => {
    return () => {
      if (previewPdfUrlRef.current) URL.revokeObjectURL(previewPdfUrlRef.current);
    };
  }, []);

  const rubroByNro = useMemo(() => {
    const m = new Map<number, NoImputable>();
    rubroLookup.forEach((r) => m.set(r.nroRubro, r));
    return m;
  }, [rubroLookup]);

  const totalizaEnLabel = (totalizaEn: number | undefined | null): string => {
    if (totalizaEn == null || totalizaEn === 0) return '-';
    const p = rubroByNro.get(totalizaEn);
    return p ? formatNoImputableLabel(p) : String(totalizaEn);
  };

  useEffect(() => {
    // Saltear la primera ejecución para no resetear la página restaurada de sessionStorage
    if (!isMounted.current) return;
    const timeoutId = setTimeout(() => {
      setPage(0);
      saveState(0, rowsPerPage, searchTerm);
      fetchNoImputables(0, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    saveState(newPage, rowsPerPage, searchTerm);
    fetchNoImputables(newPage, rowsPerPage, searchTerm);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    saveState(0, newRowsPerPage, searchTerm);
    fetchNoImputables(0, newRowsPerPage, searchTerm);
  };

  const handleDelete = async (nroRubro: number) => {
    if (window.confirm('¿Está seguro de eliminar este no imputable?')) {
      try {
        await noImputableService.deleteNoImputable(nroRubro);
        enqueueSnackbar('No imputable eliminado exitosamente', { variant: 'success' });
        refreshRubroLookup();
        fetchNoImputables();
      } catch (error: any) {
        console.error('Error al eliminar no imputable:', error);
        enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar no imputable', { variant: 'error' });
      }
    }
  };

  const handleClosePdfPreview = () => {
    setPreviewPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const handleInformePdfPlanCuentas = async () => {
    try {
      setExportingPdf(true);
      const [rowsImp, rowsNoImp, companias] = await Promise.all([
        imputableService.getAllImputablesPlanCuentas(),
        noImputableService.getAllNoImputablesPlanCuentas(),
        companiaService.getCompanias({ pageNumber: 1, pageSize: 100 }).catch(() => [])
      ]);

      if (!rowsImp.length && !rowsNoImp.length) {
        enqueueSnackbar('No hay datos para el informe.', { variant: 'warning' });
        return;
      }

      const empresa = companias[0];
      const empresaNombre = empresa?.nombre?.trim() || '—';
      const empresaLocalidad = empresa?.localidad?.trim() || '—';
      const empresaProvincia = empresa?.provincia?.trim() || '—';

      const noImpMap = new Map<number, NoImputable>(rowsNoImp.map((n) => [n.nroRubro, n]));
      const byRubro = new Map<number, typeof rowsImp>();
      for (const r of rowsImp) {
        const key = r.nroRubro ?? 0;
        const list = byRubro.get(key) ?? [];
        list.push(r);
        byRubro.set(key, list);
      }

      const rubroSet = new Set<number>();
      rowsNoImp.forEach((n) => rubroSet.add(n.nroRubro));
      rowsImp.forEach((r) => rubroSet.add(r.nroRubro ?? 0));
      const rubrosOrdenados = Array.from(rubroSet).sort((a, b) => a - b);

      const rubroBand: [number, number, number] = [236, 245, 255];

      type BodyEl = string | { content: string | number; styles?: Record<string, unknown> };
      type BodyRowKind = 'rubro' | 'cuenta';
      const body: BodyEl[][] = [];
      const bodyRowKinds: BodyRowKind[] = [];

      for (const rubro of rubrosOrdenados) {
        const ni = noImpMap.get(rubro);
        body.push([
          {
            content: String(rubro),
            styles: {
              fillColor: rubroBand,
              fontStyle: 'bold',
              fontSize: 9,
              halign: 'left' as const,
              cellPadding: 4
            }
          },
          {
            content: formatoNombreRubroReporte(ni, rubro),
            styles: { fillColor: rubroBand, fontStyle: 'bold', fontSize: 9, cellPadding: 4 }
          },
          { content: '', styles: { fillColor: rubroBand, cellPadding: 4 } }
        ]);
        bodyRowKinds.push('rubro');

        const imps = [...(byRubro.get(rubro) ?? [])].sort(
          (a, b) => Number(a.codImputable) - Number(b.codImputable)
        );

        for (const imp of imps) {
          body.push([
            imp.codImputable || '',
            (imp.nombre || '').trim(),
            visibleEnReporte(imp.visibleEn)
          ]);
          bodyRowKinds.push('cuenta');
        }
      }

      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;

      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const marginX = 42;
      const marginBottom = 40;
      const headerTop = 38;
      const headerBlockHeight = 72;
      const marginTop = headerTop + headerBlockHeight;

      const today = new Date();
      const fechaStr = today.toLocaleDateString('es-AR');
      const horaStr = today.toLocaleTimeString('es-AR', { hour: 'numeric', minute: '2-digit', hour12: true });

      const metaHeader = () => {
        doc.setTextColor(0);
        doc.setFontSize(15);
        doc.text('Plan De Cuentas', marginX, headerTop);
        doc.setFontSize(10);
        doc.text(`Empresa: ${empresaNombre}`, marginX, headerTop + 20);
        doc.text(`Localidad: ${empresaLocalidad}`, marginX, headerTop + 34);
        doc.text(`Provincia: ${empresaProvincia}`, marginX, headerTop + 48);
      };

      const padBase = { top: 4, bottom: 4, right: 5 } as const;
      const padRubroCol0 = { ...padBase, left: 2 };
      const padCuentaCol0 = { ...padBase, left: 18 };

      autoTable(doc, {
        margin: { left: marginX, right: marginX, top: marginTop, bottom: marginBottom },
        showHead: 'everyPage',
        theme: 'plain',
        tableLineWidth: 0,
        styles: {
          fontSize: 9,
          cellPadding: { top: 4, bottom: 4, left: 5, right: 5 },
          valign: 'middle',
          overflow: 'linebreak',
          lineWidth: 0
        },
        headStyles: {
          fillColor: [245, 245, 248],
          textColor: [30, 30, 35],
          fontStyle: 'bold',
          fontSize: 9,
          lineWidth: 0
        },
        columnStyles: {
          0: { cellWidth: 96, halign: 'left' },
          1: { cellWidth: 312 },
          2: { cellWidth: 103, halign: 'left' }
        },
        head: [['Nro Cuenta', 'Descripcion', 'Visible en...']],
        body,
        tableWidth: 'wrap',
        didParseCell: (data) => {
          if (data.section !== 'body' || data.column.index !== 0) return;
          const kind = bodyRowKinds[data.row.index];
          if (kind === 'rubro') {
            data.cell.styles.cellPadding = padRubroCol0;
          } else if (kind === 'cuenta') {
            data.cell.styles.cellPadding = padCuentaCol0;
          }
        },
        willDrawPage: () => {
          metaHeader();
        },
        didDrawPage: (data) => {
          const docRef = data.doc;
          const pageH =
            typeof docRef.internal.pageSize.getHeight === 'function'
              ? docRef.internal.pageSize.getHeight()
              : (docRef.internal.pageSize as unknown as { height: number }).height;
          docRef.setFontSize(8);
          docRef.setTextColor(90);
          docRef.text(`Fecha: ${fechaStr} Hora:${horaStr} Pagina ${data.pageNumber}`, marginX, pageH - marginBottom / 2);
          docRef.setTextColor(0);
        }
      });

      const pdfBlob = doc.output('blob');
      const blob =
        pdfBlob instanceof Blob && pdfBlob.type === 'application/pdf'
          ? pdfBlob
          : new Blob([pdfBlob], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      setPreviewPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      enqueueSnackbar('Vista previa del PDF lista.', { variant: 'success' });
    } catch (error) {
      console.error('Error al generar PDF del plan de cuentas:', error);
      enqueueSnackbar('No se pudo generar el PDF.', { variant: 'error' });
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      const allItems = await noImputableService.getAllNoImputablesPlanCuentas();
      const dataToExport = allItems.map((item) => ({
        'Código': item.nroRubro,
        'Nombre': (item.nombreCuenta || item.descripcioncta || '').trim(),
        'Nivel': item.segmento,
        'Totaliza en': item.totalizaEn ?? ''
      }));
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'NoImputables');
      XLSX.writeFile(wb, 'NoImputables.xlsx');
      enqueueSnackbar('Archivo exportado exitosamente', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar:', error);
      enqueueSnackbar('Error al exportar datos', { variant: 'error' });
    }
  };

  const handleImportExcel = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);

        for (const row of data) {
          const r = row as Record<string, unknown>;
          const nroRubroRaw = r['Código'] ?? r['NroRubro'] ?? r['Nro Rubro'];
          const segmentoRaw = r['Nivel'] ?? r['Segmento'];
          const totalizaEnRaw = r['Totaliza en'] ?? r['TotalizaEn'];

          const nroRubro = Number(nroRubroRaw);
          const segmento = Number(segmentoRaw);
          const totalizaEnNum = Number(totalizaEnRaw);

          const noImputable: NoImputable = {
            nroRubro: Number.isFinite(nroRubro) ? nroRubro : 0,
            descripcioncta: String(r['Descripcion'] ?? r['Descripción'] ?? r['Nombre'] ?? '').trim(),
            nombreCuenta: String(r['Nombre'] ?? '').trim(),
            segmento: Number.isFinite(segmento) ? segmento : 0,
            totalizaEn: Number.isFinite(totalizaEnNum) && totalizaEnNum > 0 ? totalizaEnNum : undefined,
            saldo: 0
          };

          await noImputableService.createNoImputable(noImputable);
        }

        enqueueSnackbar('Datos importados exitosamente', { variant: 'success' });
        refreshRubroLookup();
        fetchNoImputables(0, rowsPerPage, searchTerm);
      } catch (error) {
        console.error('Error al importar:', error);
        enqueueSnackbar('Error al importar datos', { variant: 'error' });
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              No Imputables
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleInformePdfPlanCuentas}
                disabled={exportingPdf}
              >
                {exportingPdf ? 'Generando…' : 'Plan de cuentas'}
              </Button>
              <Button variant="outlined" color="primary" startIcon={<FileDownloadIcon />} onClick={handleExportExcel}>
                Exportar
              </Button>
              <Button variant="outlined" color="primary" component="label" startIcon={<FileUploadIcon />}>
                Importar
                <input type="file" hidden accept=".xlsx,.xls" onChange={handleImportExcel} />
              </Button>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/contabilidad/noimputables/nuevo')}>
                Nuevo No Imputable
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {noImputables.map((noImputable) => (
                <Card key={noImputable.nroRubro} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {noImputable.nroRubro} - {(noImputable.nombreCuenta || noImputable.descripcioncta || '').trim() || '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Nivel: {noImputable.segmento}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Totaliza en: {totalizaEnLabel(noImputable.totalizaEn)}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => { saveState(page, rowsPerPage, searchTerm); navigate(`/contabilidad/noimputables/${noImputable.nroRubro}`); }} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(noImputable.nroRubro)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nivel</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 220 }}>Totaliza en</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {noImputables.map((noImputable) => (
                    <TableRow key={noImputable.nroRubro} hover>
                      <TableCell>{noImputable.nroRubro}</TableCell>
                      <TableCell>{(noImputable.nombreCuenta || noImputable.descripcioncta || '').trim() || '-'}</TableCell>
                      <TableCell>{noImputable.segmento}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{totalizaEnLabel(noImputable.totalizaEn)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => { saveState(page, rowsPerPage, searchTerm); navigate(`/contabilidad/noimputables/${noImputable.nroRubro}`); }} color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => handleDelete(noImputable.nroRubro)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {previewPdfUrl ? (
        <PDFViewer
          pdfUrl={previewPdfUrl}
          onClose={handleClosePdfPreview}
          title="Plan de cuentas"
          documentId="plan-cuentas-preview"
          documentType="recibo"
          downloadFileName="Plan de cuentas Report.pdf"
        />
      ) : null}
    </Box>
  );
};

export default NoImputables;
